// SPDX-License-Identifier: BUSL-1.1

/**
 * Sidebar navigation with governance-focused section groups.
 *
 * Renders nav items grouped by domain (Overview, Governance, Infrastructure,
 * Settings) with active-state highlighting, lucide icons, and optional badge
 * counts per item.
 *
 * Phase 0 restructure per docs/NAV-GOVERNANCE-ROADMAP.md:
 * - Merged Config + Agent Governance → Governance
 * - Renamed Operators → Fleet (separate page)
 * - Renamed Runner Platform → Infrastructure
 * - Moved Teams/API Keys → Settings
 * - Audit Log hidden (accessible from detail pages)
 * - Org switcher in top bar (not sidebar)
 */

import {
  type LucideIcon,
  LayoutDashboard,
  FileCode,
  ToggleLeft,
  Bot,
  Inbox,
  Users,
  KeyIcon,
  Activity,
  Server,
  BarChart3,
  UserCog,
  GitFork,
} from 'lucide-react';
import { NavLink } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { apiFetch, shouldRetryApiQuery } from '@/lib/api-client';
import {
  AGENTS_QUERY_KEY,
  MODERATION_PENDING_COUNT_QUERY_KEY,
  queryKeyLabel,
} from '@/lib/bootstrap-query-keys';
import { useOrg } from '@/contexts/org-context';
import { safeCount } from '@build-ceo/shared-constants';
import type { Agent } from '@/types/api';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

/** Role gate — item shown only when user's membershipRole meets the threshold. */
type RoleGate = 'owner' | 'admin' | 'member' | 'viewer';

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  /** Minimum role required to see this item. undefined = visible to all authenticated users. */
  minRole?: RoleGate;
}

interface NavGroup {
  label: string;
  items: ReadonlyArray<NavItem>;
}

interface NavBadge {
  count: number;
  variant: 'red' | 'amber' | 'blue';
}

/* ------------------------------------------------------------------ */
/*  Navigation structure                                               */
/* ------------------------------------------------------------------ */

// Role priority — higher index = more privileged
const ROLE_ORDER: RoleGate[] = ['viewer', 'member', 'admin', 'owner'];

/** Returns true when userRole meets (or exceeds) the required minRole threshold. */
function hasRole(userRole: string | null | undefined, minRole?: RoleGate): boolean {
  if (!minRole) return true;
  if (!userRole) return false;
  return ROLE_ORDER.indexOf(userRole as RoleGate) >= ROLE_ORDER.indexOf(minRole);
}

const NAV_GROUPS: ReadonlyArray<NavGroup> = [
  {
    label: 'Overview',
    items: [{ to: '/', label: 'Dashboard', icon: LayoutDashboard }],
  },
  {
    label: 'Governance',
    items: [
      { to: '/configs', label: 'Configs', icon: FileCode },
      { to: '/flags', label: 'Feature Flags', icon: ToggleLeft },
      { to: '/agents', label: 'Agents', icon: Bot },
      { to: '/timeline', label: 'Timeline', icon: Activity },
      { to: '/operators', label: 'Fleet', icon: Activity },
      { to: '/inbox', label: 'Review Queue', icon: Inbox, minRole: 'admin' },
    ],
  },
  {
    label: 'Infrastructure',
    items: [
      { to: '/tenant-runner-pool', label: 'Runners', icon: Server, minRole: 'admin' },
      { to: '/tenant-settings', label: 'Cloud Provider', icon: Server, minRole: 'admin' },
    ],
  },
  {
    label: 'Settings',
    items: [
      { to: '/members', label: 'Members', icon: UserCog, minRole: 'admin' },
      { to: '/teams', label: 'Teams', icon: Users, minRole: 'admin' },
      { to: '/github-settings', label: 'GitHub', icon: GitFork, minRole: 'admin' },
      { to: '/usage', label: 'Usage', icon: BarChart3 },
      { to: '/keys', label: 'API Keys', icon: KeyIcon },
    ],
  },
];

/* ------------------------------------------------------------------ */
/*  Badge rendering                                                    */
/* ------------------------------------------------------------------ */

function NavBadgePill({ badge }: { badge: NavBadge }) {
  if (badge.count <= 0) return null;

  const colorClass =
    badge.variant === 'red'
      ? 'bg-red-900/30 text-red-400'
      : badge.variant === 'amber'
        ? 'bg-amber-900/30 text-amber-400'
        : 'bg-blue-900/30 text-blue-400';

  return (
    <span
      className={`ml-auto rounded px-1.5 font-mono text-[10px] font-semibold ${colorClass}`}
    >
      {badge.count}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/*  Hooks                                                              */
/* ------------------------------------------------------------------ */

function useSidebarBadges(enabled: boolean): Record<string, NavBadge | undefined> {
  const { data: agentsResponse } = useQuery({
    queryKey: AGENTS_QUERY_KEY,
    enabled,
    queryFn: () =>
      apiFetch<{ data: Agent[] }>('/agents', { _queryKey: queryKeyLabel(AGENTS_QUERY_KEY) }),
    staleTime: 30_000,
    retry: shouldRetryApiQuery,
  });

  const { data: pendingCount } = useQuery({
    queryKey: MODERATION_PENDING_COUNT_QUERY_KEY,
    enabled,
    queryFn: () =>
      apiFetch<{ count: number }>('/moderation/pending-count', {
        _queryKey: queryKeyLabel(MODERATION_PENDING_COUNT_QUERY_KEY),
      }),
    staleTime: 15_000,
    retry: shouldRetryApiQuery,
  });

  // NocoDB #4600: Use safeCount to prevent SPA crash if agentsResponse.data is not an array
  const failedAgentCount = safeCount(agentsResponse?.data, (a: Agent) => a.status === 'error');

  return {
    '/agents': { count: failedAgentCount, variant: 'red' },
    '/inbox': { count: pendingCount?.count ?? 0, variant: 'blue' },
  };
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export function SidebarNav() {
  const { currentOrg, bootstrapReady } = useOrg();
  const badges = useSidebarBadges(bootstrapReady);
  const currentRole = currentOrg?.membershipRole ?? null;

  return (
    <nav className="flex flex-col">
      {NAV_GROUPS.map((group) => {
        const visibleItems = group.items.filter((item) =>
          hasRole(currentRole, item.minRole),
        );
        if (visibleItems.length === 0) return null;

        return (
          <div key={group.label} className="flex flex-col gap-1">
            <div className="px-3 pb-1 pt-4 text-[10px] font-semibold uppercase tracking-wider text-neutral-500">
              {group.label}
            </div>
            {visibleItems.map(({ to, label, icon: Icon }) => {
              const badge = badges[to];
              return (
                <NavLink
                  key={to}
                  to={to}
                  end={to === '/'}
                  className={({ isActive }) =>
                    `inline-flex items-center gap-2 rounded px-3 py-2 text-sm transition-colors ${
                      isActive
                        ? 'bg-neutral-800 text-white'
                        : 'text-neutral-400 hover:bg-neutral-800/50 hover:text-neutral-200'
                    }`
                  }
                >
                  <Icon className="size-4" />
                  <span>{label}</span>
                  {badge ? <NavBadgePill badge={badge} /> : null}
                </NavLink>
              );
            })}
          </div>
        );
      })}
    </nav>
  );
}
